import { ui } from '@procuros/datachecks';
import { ColumnDef, ColumnHelper, ColumnMeta } from '@tanstack/react-table';

import { logError } from 'services/logging/logging';
import { isDefined } from 'support/helpers/generic/generic';
import { ColumnRequirement, ColumnTypesInternal, WebEDIRowData } from 'support/types/webediTable';
import { cellMapping } from 'components/DataTable/cellMapping';
import { HeaderCell } from 'components/DataTable/cells/HeaderCell';
import { BodyCell } from 'components/DataTable/cells/WebEDI/BodyCell/BodyCell';
import { createIsReadOnly } from 'components/DataTable/cells/WebEDI/cellHelpers';
import { indexColumn } from 'components/DataTable/columns/indexColumn';
import { selectionPartial } from 'components/DataTable/columns/selectionPartial';
import { deletionPartial } from 'components/DataTable/columns/WebEDI/deletionPartial';

import { DeleteEmbeddedRowCell } from '../../cells/WebEDI/CreateEdit/DeleteEmbeddedRowCell';
import { CollapseEmbeddedRowsCell } from '../../cells/WebEDI/EmbeddedTable/CollapseEmbeddedRowsCell';
import { GenerateAllColumnsProps, GenerateMultiColumnProps, GenerateProps, Style } from './types';

export const concatenateColumnPaths = (parentPath: string, childPath: string): string => {
  if (/\[\d+\]$/.test(parentPath)) {
    const cleanedChildPath = childPath.replace(/^\*\./, '');
    return `${parentPath}.${cleanedChildPath}`;
  }
  return `${parentPath}.${childPath}`;
};

export const createColumns = (
  config: Array<ui.Field>,
  columnHelper: ColumnHelper<WebEDIRowData>,
  selectableRows: boolean,
  deletableRows: boolean,
  enableEmbeddedRowActions: boolean,
  deleteQuantityField?: string,
  nestedLevelField?: string,
  withIndexColumn?: boolean,
): Array<ColumnDef<WebEDIRowData>> => {
  const columns = generateAllColumns({
    config,
    columnHelper,
    selectableRows,
    deletableRows,
    enableEmbeddedRowActions,
    nestedLevelField,
  });

  if (withIndexColumn && !hasColumn(columns, ColumnTypesInternal.procuros_index)) {
    columns.unshift(indexColumn(columnHelper));
  }

  if (deletableRows && !hasColumn(columns, ColumnTypesInternal.procuros_delete)) {
    columns.unshift(deletionPartial(deleteQuantityField, columnHelper));
  }
  if (selectableRows && !hasColumn(columns, ColumnTypesInternal.procuros_select)) {
    columns.unshift(selectionPartial(columnHelper));
  }

  return columns;
};

const hasTypeConfig = (field: ui.Field): field is ui.NumberField | ui.EnumerableField => {
  return 'type_config' in field;
};

const shouldSkipField = (field: ui.Field): boolean => {
  return field.is_hidden || field.modality === 'NOT_USED';
};

const getFieldRequirement = (field: ui.Field): ColumnRequirement => {
  if (field.modality === 'MANDATORY') {
    return 'required';
  }
  if (field.modality === 'CONDITIONAL') {
    return 'conditional';
  }
  return 'optional';
};

const hasColumn = (columns: Array<ColumnDef<WebEDIRowData>>, columnId: string): boolean => {
  return columns.some(({ id }) => id === columnId);
};

const getTextAlign = (
  optionalConfig: { textAlign?: string },
  columnType: ui.FieldTypes,
): 'right' | 'left' | 'center' => {
  if (optionalConfig && 'textAlign' in optionalConfig) {
    const align = optionalConfig.textAlign;
    if (align === 'right' || align === 'left' || align === 'center') {
      return align;
    }
  }
  return [ui.FieldTypes.number, ui.FieldTypes.monetary].includes(columnType) ? 'right' : 'left';
};

const generateColumn = ({
  columnProps,
  columnHelper,
  selectableRows,
  headerMenuActions,
  nestedLevelField,
}: GenerateProps<WebEDIRowData>): ColumnDef<WebEDIRowData> | null => {
  const columnType = columnProps.type;
  const [ViewComponent, EditComponent] = cellMapping[columnType];
  if (!ViewComponent || !EditComponent) {
    logError(`No cell mapping for column type: ${columnType}`);
    return null;
  }

  const optionalConfig = hasTypeConfig(columnProps)
    ? { ...columnProps.type_config, ...columnProps.extraFields, expression: columnProps.jsonata_expression }
    : { ...columnProps.extraFields, expression: columnProps.jsonata_expression };

  const textAlign = getTextAlign(optionalConfig, columnType);

  const style = {
    textAlign,
    width: columnProps.extraFields?.width,
  };
  const requirement = getFieldRequirement(columnProps);
  const required = requirement === 'required';

  const meta = {
    type: columnProps.type,
    label: columnProps.label,
    description: columnProps.description,
    isReadOnly: createIsReadOnly(
      Boolean(columnProps.is_readonly),
      Boolean(columnProps.extraFields?.packingListEnabled || columnProps.extraFields?.forceReadOnly),
      Boolean(columnProps.extraFields?.forceReadOnlyUnlessError),
    ),
    requirement,
    optionalConfig,
    style,
    rightAddon: columnProps.extraFields?.rightAddon,
    rightAddonWidth: columnProps.extraFields?.rightAddonWidth,
    leftAddon: columnProps.extraFields?.leftAddon,
    showNestLevel: columnProps.path === nestedLevelField,
    disableIfNested: columnProps.extraFields?.disabledIfNested,
    packingListEnabled: Boolean(columnProps.extraFields?.packingListEnabled),
    rowConfig: columnProps.row_config,
  };

  if (!columnProps.path) {
    const expressionKey = `expression_${columnProps.jsonata_expression}`;
    return columnHelper.accessor(expressionKey as keyof WebEDIRowData, {
      id: expressionKey,
      header: (props) => (
        <HeaderCell
          {...props}
          tooltip={columnProps.description}
          required={required}
          mode={props.table.options.meta?.mode}
          selectableRows={selectableRows}
          actions={headerMenuActions}
        >
          {columnProps.label || columnProps.path}
        </HeaderCell>
      ),
      cell: BodyCell,
      footer: undefined,
      meta,
    });
  }

  return columnHelper.accessor(columnProps.path as keyof WebEDIRowData, {
    id: columnProps.path,
    size: columnProps.extraFields?.width,
    header: (props) => (
      <HeaderCell
        {...props}
        tooltip={columnProps.description}
        required={required}
        mode={props.table.options.meta?.mode}
        selectableRows={selectableRows}
        actions={headerMenuActions}
      >
        {columnProps.label || columnProps.path}
      </HeaderCell>
    ),
    cell: BodyCell,
    meta,
  });
};

const generateAllColumns = ({
  config,
  columnHelper,
  selectableRows,
  enableEmbeddedRowActions,
  nestedLevelField,
}: GenerateAllColumnsProps<WebEDIRowData>): Array<ColumnDef<WebEDIRowData>> => {
  return config
    .map((columnProps) => {
      if (columnProps.type === ui.FieldTypes.multi_column) {
        return generateMultiColumnGroup({ columnProps, columnHelper, selectableRows, enableEmbeddedRowActions });
      }
      return generateColumn({ columnProps, columnHelper, selectableRows, nestedLevelField });
    })
    .flat()
    .filter(isDefined);
};

const createEmbeddedDeleteColumn = (
  columnHelper: ColumnHelper<WebEDIRowData>,
  path: string,
): ColumnDef<WebEDIRowData> => {
  return columnHelper.display({
    id: `${ColumnTypesInternal.procuros_delete}_${path}`,
    meta: {
      type: ColumnTypesInternal.procuros_delete,
    },
    cell: DeleteEmbeddedRowCell,
  });
};

const addEmbeddedDeleteColumn = (
  columns: Array<ColumnDef<WebEDIRowData>>,
  columnHelper: ColumnHelper<WebEDIRowData>,
  path: string,
): void => {
  const deleteColumn = createEmbeddedDeleteColumn(columnHelper, path);
  columns.splice(1, 0, deleteColumn);
};

/**
 * Leading toggle that hides or shows this group's items for one row.
 *
 * It lives inside the group so each embedded table can be collapsed independently, and it renders
 * its own cell next to the collapsed summary — otherwise a collapsed group could never be reopened.
 *
 * Present in every display mode. While collapsed in create/edit the group's add row and delete
 * buttons are hidden with its items, but the values stay in the page form, so expanding brings
 * back any unsaved edit.
 */
const createEmbeddedCollapseColumn = (
  columnHelper: ColumnHelper<WebEDIRowData>,
  path: string,
): ColumnDef<WebEDIRowData> => {
  return columnHelper.display({
    id: `${ColumnTypesInternal.procuros_collapse}_${path}`,
    meta: {
      type: ColumnTypesInternal.procuros_collapse,
    },
    cell: CollapseEmbeddedRowsCell,
  });
};

/**
 * Must stay the group's FIRST column: `planCellRender` decides which cell carries the group's wide
 * summary by checking that the previous cell is not a same-group data cell, which only holds while
 * the toggle leads the group.
 */
const addEmbeddedCollapseColumn = (
  columns: Array<ColumnDef<WebEDIRowData>>,
  columnHelper: ColumnHelper<WebEDIRowData>,
  path: string,
): void => {
  columns.unshift(createEmbeddedCollapseColumn(columnHelper, path));
};

const createGroupColumn = ({
  id,
  columnProps,
  columnHelper,
  selectableRows,
  columns,
}: {
  id: string;
  columnProps: ui.MultiColumnField;
  columnHelper: ColumnHelper<WebEDIRowData>;
  selectableRows?: boolean;
  columns: Array<ColumnDef<WebEDIRowData>>;
}): ColumnDef<WebEDIRowData> => {
  const requirement = getFieldRequirement(columnProps);
  const required = requirement === 'required';
  const style: Style = {
    textAlign: 'center',
  };

  const meta: ColumnMeta<WebEDIRowData, unknown> = {
    type: columnProps.type,
    label: columnProps.label,
    description: columnProps.description,
    isReadOnly: createIsReadOnly(Boolean(columnProps.is_readonly), false),
    requirement,
    addRowButtonLabel: columnProps.type_config.addRowButtonLabel,
    style,
  };

  return columnHelper.group({
    id,
    header: (props) => (
      <HeaderCell
        {...props}
        tooltip={columnProps.description}
        required={required}
        mode={props.table.options.meta?.mode}
        selectableRows={selectableRows}
      >
        {columnProps.label}
      </HeaderCell>
    ),
    columns,
    meta,
  });
};

const generateNestedMultiColumnGroup = ({
  nestedMultiColumn,
  columnHelper,
  selectableRows,
  enableEmbeddedRowActions,
}: {
  nestedMultiColumn: ui.MultiColumnField;
  columnHelper: ColumnHelper<WebEDIRowData>;
  selectableRows?: boolean;
  enableEmbeddedRowActions?: boolean;
}): ColumnDef<WebEDIRowData> | null => {
  const concatenatedPath = nestedMultiColumn.path;

  const innerColumns: Array<ColumnDef<WebEDIRowData> | null> = [];
  nestedMultiColumn.type_config.fields.forEach((innerField) => {
    if (shouldSkipField(innerField)) {
      return;
    }

    const innerModifiedProps: ui.Field = {
      ...innerField,
      path: concatenateColumnPaths(concatenatedPath, innerField.path),
    };

    const innerColumn = generateColumn({
      columnProps: innerModifiedProps,
      columnHelper,
      selectableRows,
    });

    innerColumns.push(innerColumn);
  });

  const validInnerColumns = innerColumns.filter(isDefined);

  if (validInnerColumns.length === 0) {
    return null;
  }

  if (enableEmbeddedRowActions) {
    addEmbeddedDeleteColumn(validInnerColumns, columnHelper, concatenatedPath);
  }

  addEmbeddedCollapseColumn(validInnerColumns, columnHelper, concatenatedPath);

  return createGroupColumn({
    id: concatenatedPath,
    columnProps: nestedMultiColumn,
    columnHelper,
    selectableRows,
    columns: validInnerColumns,
  });
};

const generateMultiColumnGroup = ({
  columnProps,
  columnHelper,
  selectableRows,
  enableEmbeddedRowActions,
}: GenerateMultiColumnProps<WebEDIRowData>): Array<ColumnDef<WebEDIRowData>> => {
  const columns: Array<ColumnDef<WebEDIRowData> | null> = [];
  const hasNestedMultiColumn = columnProps.type_config.fields.some(
    (field) => field.type === ui.FieldTypes.multi_column && !shouldSkipField(field),
  );

  columnProps.type_config.fields.forEach((subColumnProps) => {
    if (shouldSkipField(subColumnProps)) {
      return null;
    }

    const modifiedColumnProps: ui.Field = {
      ...subColumnProps,
      path: concatenateColumnPaths(columnProps.path, subColumnProps.path),
    };

    if (subColumnProps.type === ui.FieldTypes.multi_column) {
      const nestedMultiColumn = modifiedColumnProps as ui.MultiColumnField;

      const groupColumn = generateNestedMultiColumnGroup({
        nestedMultiColumn,
        columnHelper,
        selectableRows,
        enableEmbeddedRowActions,
      });

      if (groupColumn) {
        columns.push(groupColumn);
      }
      return;
    }

    const column = generateColumn({
      columnProps: modifiedColumnProps,
      columnHelper,
      selectableRows,
    });

    columns.push(column);
  });

  const validColumns = columns.filter(isDefined);

  if (validColumns.length === 0) {
    return [];
  }

  if (hasNestedMultiColumn) {
    return validColumns;
  }

  if (enableEmbeddedRowActions) {
    addEmbeddedDeleteColumn(validColumns, columnHelper, columnProps.path);
  }

  addEmbeddedCollapseColumn(validColumns, columnHelper, columnProps.path);

  return [
    createGroupColumn({
      id: columnProps.path,
      columnProps,
      columnHelper,
      selectableRows,
      columns: validColumns,
    }),
  ];
};
